import type { ConvertedMoneyDto } from './currency.js';
import type { Currency } from './currency.js';
import type { OfferCapabilities } from './capabilities.js';
import type { ChatSessionIntegrationResult } from './llm-chat.js';
import type { OfferAiReviewRunSummaryDto } from './offer-ai-review.js';
import { ProfileDto } from './profile.js';

export const OfferStatus = {
  Draft: 'draft',
  InReview: 'in-review',
  Submitted: 'submitted',
  Accepted: 'accepted',
  Rejected: 'rejected',
}

export const OfferStatusValues = Object.values(OfferStatus)

export type OfferStatus = (typeof OfferStatus)[keyof typeof OfferStatus]

export enum PricingModel {
  TimeAndMaterial = 'timeAndMaterial',
  FixedPrice = 'fixedPrice',
}

export interface OfferRiskDto {
  id?: string;
  risk: string;
  probability?: number;
  impact?: number;
  mitigation?: string;
  order?: number;
}

export interface OfferLineItemDto {
  id?: string;
  item: string;
  quantity: number;
  unit: string;
  externalBusinessRate: number;
  internalBusinessRate?: number;
  total?: number;
  order?: number;
}

export interface OfferMilestoneDto {
  id?: string;
  name: string;
  startDate?: string;
  output: string;
  acceptanceCriteria: string;
  requiredCooperation?: string;
  dueDate?: string;
  price?: number | null;
  order?: number;
}

export interface OfferTeamMemberDto {
  id?: string;
  role: string;
  seniority: string;
  order?: number;
}

export interface DeliverableDto {
  id: string;
  name: string;
}

export interface OfferAttachmentDto {
  id: string;
  offerId: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  description?: string;
  order?: number;
}

export interface UploadOfferAttachmentDto {
  description?: string;
}

export interface ReorderOfferAttachmentsDto {
  attachmentIds: string[];
}

export interface OfferDto {
  id: string;
  inquiryId: string;
  accountId: string;
  createdByUserId: string;
  status: OfferStatus;
  proposal?: string;
  keyContact?: ProfileDto;
  outOfScope?: string;
  caseStudyIds?: string[];
  pricingModel: PricingModel;
  lineItems: OfferLineItemDto[];
  milestones: OfferMilestoneDto[];
  team?: OfferTeamMemberDto[];
  risks?: OfferRiskDto[];
  sla?: string;
  validityDate?: string;
  totalAmount?: number;
  currency: Currency;
  totalAmountConverted?: ConvertedMoneyDto;
  createdAt: string;
  updatedAt: string;
  inquiryVersionNumber: number;
  attachments?: OfferAttachmentDto[];
  // Inquirer-only evaluation metadata (visible to inquirer owners and SUPERADMIN/SUPPORT)
  inquirerNotes?: string;
  inquirerDecision?: OfferEvaluationDecision;
  /** Inquirer-only: -2 (lowest) … 2 (highest); default 0 */
  preference?: number;
  /** Set when finalizing evaluation for a rejected offer (inquirer-facing codes) */
  inquirerRejectionReasonCodes?: string[];
  /** Required when `other` is among inquirerRejectionReasonCodes */
  inquirerRejectionCustomReason?: string | null;
  // Admin rejection reason (when offer is rejected from review)
  rejectionReason?: string;
  /** Inquirer/admin: latest AI review of offer vs inquiry version (omit for consultant responses). */
  aiReview?: OfferAiReviewRunSummaryDto | null;
  /** Present on create when chatSessionId was sent in the request. */
  chatSessionIntegration?: ChatSessionIntegrationResult;
  /** Server-computed action flags for the current user. */
  capabilities?: OfferCapabilities;
}

export interface CreateOfferDto {
  proposal?: string;
  outOfScope?: string;
  caseStudyIds?: string[];
  pricingModel: PricingModel;
  lineItems: OfferLineItemDto[];
  milestones: OfferMilestoneDto[];
  team?: OfferTeamMemberDto[];
  risks?: Array<{ risk: string; probability?: number; impact?: number; mitigation?: string }>;
  sla?: string;
  validityDate?: string; // YYYY-MM-DD
  currency: Currency;
  totalAmount?: number;
  submit?: boolean; // if true, set status to InReview (pending admin approval)
  copyFromPreviousOffer?: boolean; // if true, copy data from previous offer version
  /** Optional LLM chat session to link and copy attachments from on create. */
  chatSessionId?: string;
}

export interface RejectOfferDto {
  rejectionReason: string;
}

export interface UpdateOfferDto extends CreateOfferDto {

}

export enum OfferEvaluationDecision {
  Undecided = 'undecided',
  Accepted = 'accepted',
  Rejected = 'rejected',
}

export interface UpdateOfferEvaluationDto {
  decision?: OfferEvaluationDecision;
  notes?: string;
  preference?: number;
}

/**
 * Line item DTO without internalBusinessRate (for inquirers)
 */
export interface OfferLineItemInquirerDto extends Omit<OfferLineItemDto, 'internalBusinessRate'> {
}

/**
 * Admin DTO - full access to all fields including evaluation metadata and internal business rates
 */
export interface OfferAdminDto extends OfferDto {
  // All fields from OfferDto are included
}

/**
 * Inquirer DTO - includes evaluation fields but excludes internalBusinessRate from line items
 */
export interface OfferInquirerDto extends Omit<OfferDto, 'lineItems'> {
  lineItems: OfferLineItemInquirerDto[];
  // Evaluation fields are included (inquirerNotes, inquirerDecision, preference)
}

/**
 * Consultant DTO - includes internalBusinessRate but excludes evaluation fields
 */
export interface OfferConsultantDto extends Omit<
  OfferDto,
  | 'inquirerNotes'
  | 'inquirerDecision'
  | 'preference'
  | 'inquirerRejectionReasonCodes'
  | 'inquirerRejectionCustomReason'
  | 'aiReview'
> {
  // Evaluation fields are excluded
  // internalBusinessRate in lineItems is included
}
